// src/pages/ProfileSettings.tsx
// Personal settings, name, title, bio, avatar.
// Avatar uploads to Supabase Storage (avatars bucket).
// These fields show up in: avatar tooltips, messaging, and firm public profile (if user is marked "show publicly").

import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../services/supabase';
import { useProfile } from '../hooks/useProfile';

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '24px 32px', minHeight: '100vh', backgroundColor: '#F5F6F8' },
  header: { fontSize: 24, fontWeight: 400, fontFamily: 'Georgia, serif', color: '#0A0A0A', marginBottom: 4 },
  subheader: { fontSize: 13, color: '#6B7280', marginBottom: 32 },
  card: {
    backgroundColor: '#FFFFFF', border: '1px solid #D0D5DD',
    borderRadius: 4, padding: 28, marginBottom: 16, maxWidth: 640,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  label: {
    fontSize: 11, color: '#6B7280', marginBottom: 6,
    letterSpacing: 0.5, textTransform: 'uppercase' as const,
  },
  input: {
    width: '100%', padding: 10, borderRadius: 3,
    backgroundColor: '#FFFFFF', color: '#0A0A0A',
    border: '1px solid #D0D5DD', fontSize: 13,
    marginBottom: 18, boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%', padding: 12, borderRadius: 3,
    backgroundColor: '#FFFFFF', color: '#0A0A0A',
    border: '1px solid #D0D5DD', fontSize: 13,
    fontFamily: 'inherit', marginBottom: 18, minHeight: 100,
    boxSizing: 'border-box' as const, resize: 'vertical' as const,
  },
  primaryBtn: {
    backgroundColor: '#0A1628', color: '#FFFFFF', border: 'none',
    padding: '10px 18px', borderRadius: 3, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  secondaryBtn: {
    backgroundColor: '#FFFFFF', color: '#3D4552',
    border: '1px solid #D0D5DD', borderRadius: 3,
    padding: '8px 14px', fontSize: 12, cursor: 'pointer',
  },
  helper: { fontSize: 11, color: '#6B7280', marginBottom: 14, marginTop: -10 },
  avatarPreview: {
    width: 80, height: 80, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 24, fontWeight: 600, color: '#FFFFFF',
    background: 'linear-gradient(135deg, #0A1628, #C9A84C)',
    overflow: 'hidden',
  },
  successMsg: {
    padding: 12, borderRadius: 3, marginBottom: 16,
    background: '#0A7C4215', color: '#0A7C42',
    fontSize: 13,
  },
  errorMsg: {
    padding: 12, borderRadius: 3, marginBottom: 16,
    background: '#C0222A15', color: '#C0222A',
    fontSize: 13,
  },
};

export default function ProfileSettings() {
  const { profile, firm, loading: profileLoading } = useProfile();

  const [fullName, setFullName] = useState('');
  const [title, setTitle] = useState('');
  const [bio, setBio] = useState('');
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setTitle((profile as any).title || '');
      setBio((profile as any).bio || '');
      setCurrentAvatarUrl(profile.avatar_url || '');
    }
  }, [profile]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Cap at 5MB to be safe
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image is too large (max 5MB).' });
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    setMessage(null);

    let avatarUrl = currentAvatarUrl;

    // Upload new photo if one was selected
    if (photoFile) {
      const ext = photoFile.name.split('.').pop();
      const path = `${profile.id}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, photoFile, { upsert: false });

      if (uploadError) {
        setSaving(false);
        setMessage({ type: 'error', text: `Photo upload failed: ${uploadError.message}` });
        return;
      }

      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
      avatarUrl = urlData.publicUrl;
    }

    const { error } = await supabase
      .from('profiles')
      .update({
        full_name: fullName.trim(),
        title: title.trim() || null,
        bio: bio.trim() || null,
        avatar_url: avatarUrl || null,
      })
      .eq('id', profile.id);

    setSaving(false);
    if (error) {
      setMessage({ type: 'error', text: `Save failed: ${error.message}` });
      return;
    }

    setCurrentAvatarUrl(avatarUrl);
    setPhotoFile(null);
    setPhotoPreview(null);
    setMessage({ type: 'success', text: 'Saved. Refreshing...' });
    // Reload so sidebar, messages, and other places using useProfile pick up the new avatar
    // setTimeout(() => window.location.reload(), 800);
    };

  const getInitials = () => {
    if (!fullName) return '?';
    return fullName.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase();
  };

  if (profileLoading) {
    return <div style={styles.container}><div style={{ fontSize: 13, color: '#6B7280' }}>Loading...</div></div>;
  }

  // Avatar to display: new file preview > existing saved URL > initials
  const displayedAvatar = photoPreview || currentAvatarUrl;

  return (
    <div style={styles.container}>
      <div style={styles.header}>Profile settings</div>
      <div style={styles.subheader}>
        Your personal information. This appears in messaging, notifications, and on your firm's public profile if you choose to be visible.
      </div>

      <div style={styles.card}>
        {/* Avatar section */}
        <div style={styles.label}>Profile photo</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 22 }}>
          <div style={styles.avatarPreview}>
            {displayedAvatar ? (
              <img src={displayedAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              getInitials()
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button onClick={() => fileRef.current?.click()} style={styles.secondaryBtn}>
              {displayedAvatar ? 'Change photo' : 'Upload photo'}
            </button>
            {displayedAvatar && (
              <button
                onClick={() => {
                  setPhotoFile(null);
                  setPhotoPreview(null);
                  setCurrentAvatarUrl('');
                }}
                style={{
                  ...styles.secondaryBtn,
                  color: '#C0222A',
                  borderColor: '#D0D5DD',
                }}
              >
                Remove photo
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
          </div>
        </div>

        <div style={styles.label}>Full name *</div>
        <input
          type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
          placeholder="Your full name" style={styles.input}
        />

        <div style={styles.label}>Title</div>
        <input
          type="text" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Chief Investment Officer" style={styles.input}
        />
        <div style={styles.helper}>Your job title, shown on your firm's public profile.</div>

        <div style={styles.label}>Bio</div>
        <textarea
          value={bio} onChange={(e) => setBio(e.target.value)}
          placeholder="A short bio, your background, experience, what you focus on at the firm."
          style={styles.textarea}
        />
        <div style={styles.helper}>Shown to allocators on your firm's public profile.</div>

        {message && (
          <div style={message.type === 'success' ? styles.successMsg : styles.errorMsg}>
            {message.text}
          </div>
        )}

        <button
          style={{ ...styles.primaryBtn, opacity: saving ? 0.5 : 1 }}
          onClick={handleSave} disabled={saving}
        >
          {saving ? 'Saving...' : 'Save changes'}
        </button>
      </div>

      {/* Read-only firm info */}
      {firm && (
        <div style={styles.card}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#0A0A0A', marginBottom: 16 }}>
            Your firm
          </div>
          <div style={styles.label}>Firm</div>
          <div style={{ fontSize: 14, color: '#0A0A0A', marginBottom: 12 }}>{firm.name}</div>
          <div style={{ fontSize: 11, color: '#6B7280' }}>
            To update firm details, go to Manage Firm.
          </div>
        </div>
      )}
    </div>
  );
}